import React, {
    useRef,
    useState,
    useEffect
}
from "react";

import HomeDocumentCard
from "./HomeDocumentCard";

import HomeDocumentScroller
from "./HomeDocumentScroller";


export default function HomeDocumentsPanel({
    directories,
    isMobile
})
{
    //--------------------------------------------------------
    // Constants
    //--------------------------------------------------------

    const CARD_HEIGHT =
        100;
		
		
	const isTablet =
		    window.innerWidth > 768
		    &&
		    window.innerWidth <= 1100;


    //--------------------------------------------------------
    // Refs
    //--------------------------------------------------------

    const contentRef =
        useRef(null);

    const targetOffset =
        useRef(0);

    const currentOffset =
        useRef(0);

    const animationRef =
        useRef(null);


    //--------------------------------------------------------
    // State
    //--------------------------------------------------------

    const [scrollPercent, setScrollPercent] =
        useState(0);

    const [documentCards, setDocumentCards] =
        useState([]);

    const [currentCard, setCurrentCard] =
        useState(0);


    //--------------------------------------------------------
    // Load configuration
    //--------------------------------------------------------

    useEffect(
        function()
        {
            loadDocumentCards();
        },
        []
    );


    //--------------------------------------------------------
    // Directories changed
    //--------------------------------------------------------

    useEffect(
        function()
        {
            if(
                directories
                &&
                directories.length > 0
            )
            {
                console.log(
                    "DIRECTORIES: ",
                    directories
                );

                updateDocumentPanels(
                    directories
                );
            }
        },
        [directories]
    );


    //--------------------------------------------------------
    // Load cards
    //--------------------------------------------------------

    async function loadDocumentCards()
    {
        try
        {
            const response =
                await fetch(
                    "config/homepagedocumentpanel.json"
                );

            if(!response.ok)
            {
                throw new Error(
                    "Unable to load document panel configuration."
                );
            }

            const json =
                await response.json();

            initialisePanels(
                json.panels
            );
        }
        catch(error)
        {
            console.error(
                error
            );
        }
    }


    //--------------------------------------------------------
    // Maximum card
    //--------------------------------------------------------

    const maxCard =
        Math.max(
            0,
            documentCards.length - 1
        );


    //--------------------------------------------------------
    // Initialise panels
    //--------------------------------------------------------

    const initialisePanels =
        function(panels)
        {
            const documentPanels =
                panels.map(
                    function(panel, index)
                    {
                        return {
                            id:
                                panel.id,

                            sequence:
                                panel.sequence,

                            image:
                                `config/homedocumentimages/${index + 1}.jpg`,

                            title:
                                "Loading...",

                            description:
                                panel.description,

                            documentCount:
                                0,

                            accentColour:
                                panel.colour,

                            directoryId:
                                null,

                            directoryName:
                                null,

                            loaded:
                                false
                        };
                    }
                );

            setDocumentCards(
                documentPanels
            );
        };


    //--------------------------------------------------------
    // Update panels
    //--------------------------------------------------------

    const updateDocumentPanels =
        function(directories)
        {
            setDocumentCards(
                function(current)
                {
                    const updatedPanels =
                        [];

                    current.forEach(
                        function(panel, index)
                        {
                            const directory =
                                directories[index];

                            if(!directory)
                            {
                                return;
                            }

                            updatedPanels.push({
                                ...panel,

                                id:
                                    directory.id,

                                title:
                                    directory.name.replace(
                                        /^\d+\./,
                                        ""
                                    ),

                                documentCount:
                                    directory.documentCount,

                                directoryId:
                                    directory.id
                            });
                        }
                    );

                    return updatedPanels;
                }
            );
        };


    //--------------------------------------------------------
    // Desktop smooth scrolling
    //--------------------------------------------------------

    useEffect(
        function()
        {
            //------------------------------------------------
            // Mobile does NOT use the document scroller
            //------------------------------------------------

            if(isMobile)
            {
                targetOffset.current =
                    0;

                currentOffset.current =
                    0;

                setCurrentCard(
                    0
                );

                setScrollPercent(
                    0
                );

                if(contentRef.current)
                {
                    contentRef.current.style.transform =
                        "none";
                }

                return;
            }


            //------------------------------------------------
            // Desktop animation
            //------------------------------------------------

            function animate()
            {
                currentOffset.current +=
                    (
                        targetOffset.current
                        -
                        currentOffset.current
                    )
                    *
                    0.12;


                const card =
                    Math.round(
                        currentOffset.current
                        /
                        CARD_HEIGHT
                    );


                setCurrentCard(
                    function(previous)
                    {
                        return previous === card
                            ?
                            previous
                            :
                            card;
                    }
                );


                if(contentRef.current)
                {
                    contentRef.current.style.transform =
                        "translateY(-"
                        +
                        currentOffset.current
                        +
                        "px)";
                }


                const maxOffset =
                    Math.max(
                        1,
                        (
                            documentCards.length - 1
                        )
                        *
                        CARD_HEIGHT
                    );


                setScrollPercent(
                    currentOffset.current
                    /
                    maxOffset
                );


                animationRef.current =
                    requestAnimationFrame(
                        animate
                    );
            }


            animate();


            return function()
            {
                if(animationRef.current)
                {
                    cancelAnimationFrame(
                        animationRef.current
                    );
                }
            };
        },
        [
            documentCards,
            isMobile
        ]
    );


    //--------------------------------------------------------
    // Scroll up
    //--------------------------------------------------------

    function scrollUp()
    {
        targetOffset.current =
            Math.max(
                0,
                targetOffset.current
                -
                CARD_HEIGHT
            );
    }


    //--------------------------------------------------------
    // Scroll down
    //--------------------------------------------------------

    function scrollDown()
    {
        const maxOffset =
            Math.max(
                0,
                (
                    documentCards.length - 1
                )
                *
                CARD_HEIGHT
            );


        targetOffset.current =
            Math.min(
                maxOffset,
                targetOffset.current
                +
                CARD_HEIGHT
            );
    }


    //========================================================
    // Styles
    //========================================================

    const containerStyle =
    {
        display:
            "flex",

        flexDirection:
            "column",

			height:
			    isMobile || isTablet
			        ?
			        "auto"
			        :
			        "calc(100vh - 150px)",

        width:
            "100%",

        overflow:
            isMobile
                ?
                "visible"
                :
                "hidden"
    };


    const contentStyle =
    {
        width:
            "100%",

        transform:
            isMobile
                ?
                "none"
                :
                undefined,

        background:
            isMobile
                ?
                "transparent"
                :
                "linear-gradient(to bottom, rgba(255,255,255,0) 80%, rgba(255,255,255,.95) 100%)"
    };
	
	
	


    //========================================================
    // Render
    //========================================================

    return (

        <div style={containerStyle}>

            <div
                ref={contentRef}
                style={contentStyle}
            >

                <div>

                    {
                        documentCards.map(
                            function(card, index)
                            {
                                return (

                                    <HomeDocumentCard

                                        id={
                                            card.id
                                        }

                                        key={
                                            index
                                        }

                                        sequence={
                                            index
                                        }

                                        image={
                                            card.image
                                        }

                                        icon={
                                            card.icon
                                        }

                                        title={
                                            card.title
                                        }

                                        description={
                                            card.description
                                        }

                                        documentCount={
                                            card.documentCount
                                        }

                                        status={
                                            card.status
                                        }

                                        accentColour={
                                            card.accentColour
                                        }

                                        delay={
                                            120
                                            +
                                            (
                                                index
                                                *
                                                85
                                            )
                                        }

                                        isMobile={
                                            isMobile
                                        }

                                    />

                                );
                            }
                        )
                    }

                </div>

            </div>


			{/* Desktop only */}

			{
			    !isMobile
			    &&
			    !isTablet
			    &&
			    <HomeDocumentScroller
			        scrollPercent={
			            scrollPercent
			        }
			        scrollUp={
			            scrollUp
			        }
			        scrollDown={
			            scrollDown
			        }
			        currentCard={
			            currentCard
			        }
			        maxCard={
			            maxCard
			        }
			    />
			}

        </div>

    );
}